import {HttpClient} from '@angular/common/http';
import { Service, inject } from '@angular/core';
import {Router} from '@angular/router';
import {Observable, Subject} from 'rxjs';
import {environment} from '../../environments/environment';
import {Group} from '../person/group';
import {PersonLogin, RoleName} from '../person/person-login';
import {Role} from '../person/role';
import {OrganizationService} from './organization-service';
import {GoogleService} from './google-service';
import {UserStateService} from './user-state.service';
import {BrandID} from '../enums/brand-id';
import {OrganizationBrandsService} from "./organization-brands-service";

@Service()
export class PersonLoginService {
  private readonly http = inject(HttpClient);
  private readonly organizationBrandsService = inject(OrganizationBrandsService);
  private readonly googleService = inject(GoogleService);
  private readonly organizationService = inject(OrganizationService);
  private readonly router = inject(Router);
  private readonly userStateService = inject(UserStateService);


  private urlBase = environment.serviceUrl;
  private expirationKey = 'loginExpiration';
  private userKey = 'userLogin';
  private isSiblingOrgUserKey = 'siblingOrgUser';
  private brandsKey: string = 'brands';
  private selectedBrandKey: string = 'selectedBrand';
  private applicationCredential: string | undefined;

  setSelectedBrand(brandID: BrandID): void {
    localStorage.setItem(this.selectedBrandKey, brandID);
  }

  getSelectedBrand(): BrandID {
    const selectedName: string = this.getSelectedBrandName();
    if (selectedName) {
      return BrandID[selectedName as keyof typeof BrandID];
    }
    return undefined!;
  }

  getSelectedBrandName(): string {
    return localStorage.getItem(this.selectedBrandKey)!;
  }

  getAccessToken(): string {
    const personLogin = this.getCachedLogin();
    return personLogin?.token || '';
  }

  getCachedLoginWithoutRedirect(): PersonLogin | undefined {
    const personLogin = localStorage.getItem(this.userKey);
    if (!personLogin || this.isLoginExpired()) {
      return undefined;
    } else {
      return JSON.parse(personLogin) as PersonLogin;
    }
  }

  getCachedLogin(): PersonLogin | undefined {
    const personLogin = localStorage.getItem(this.userKey);
    if (!personLogin || this.isLoginExpired()) {
      localStorage.clear();
      this.userStateService.redirectUri = encodeURI(this.router.url);
      this.router.navigate(['/']);
    } else {
      return JSON.parse(personLogin) as PersonLogin;
    }
    return undefined!;
  }

  getBrandIDs(): BrandID[] {
    const personLogin = this.getCachedLogin();
    let brandIDArray: BrandID[] = [];
    if (personLogin && personLogin.person) {
      const brandsString = localStorage.getItem(this.brandsKey);
      if (brandsString) {
        for (let brandString of brandsString.split(",")) {
          brandIDArray.push(BrandID[brandString as keyof typeof BrandID]);
        }
      }
    }

    return brandIDArray;
  }

  isSiblingOrgUser(): boolean {
    const result = localStorage.getItem(this.isSiblingOrgUserKey);
    return !!(result && result === 'true');
  }

  parseAuthCredential(credential: string): Observable<PersonLogin> {
    const subject = new Subject<PersonLogin>();
    const url = this.urlBase + 'personLogin/parseCredential/' + credential;
    this.http.get<PersonLogin>(url).subscribe(personLogin => {
      if (!personLogin) {
        subject.next(null!);
      } else {
        if (personLogin.person.active === false) {
          subject.error('Your account is inactive. Please contact Customer Service to have it reactivated');
        } else if (!personLogin.person.primaryOrgUnitId || personLogin.person.primaryOrgUnitId.length === 0) {
          subject.error('Please contact customer service to set a primary org for your account');
        } else {
          localStorage.setItem(GoogleService.TokenHeader, personLogin.token);
          this.organizationService.getOrganization(personLogin.person.primaryOrgUnitId).subscribe(result => {
            const primaryOrg = result;
            if (!primaryOrg || primaryOrg.active === false) {
              subject.error('Inactive primary org for your account');
            } else {
              const isSiblingOrgUser: boolean = this.organizationService.isSiblingOrg(result);
              localStorage.setItem(this.isSiblingOrgUserKey, isSiblingOrgUser.toString());
              const expirationDate: Date = new Date();
              expirationDate.setMonth(expirationDate.getMonth() + 1);
              localStorage.setItem(this.expirationKey, expirationDate.getTime().toString());
              localStorage.setItem(this.userKey, JSON.stringify(personLogin));
              subject.next(personLogin);
              this.organizationBrandsService.getByOrgUnitId(personLogin.person.primaryOrgUnitId).subscribe(orgunitXBrandsArray => {
                let brandIDArray: string[] = [];
                for (let oxb of orgunitXBrandsArray) {
                  brandIDArray.push(oxb.brandID.toString());
                }
                if (brandIDArray.length > 0) {
                  this.setSelectedBrand(BrandID[brandIDArray[0] as keyof typeof BrandID]);
                }
                this.setBrandsKey(brandIDArray);
              }, error => subject.error(error));
            }
          }, error => subject.error(error));
        }
      }
    }, error => subject.error(error));
    return subject.asObservable();
  }

  public logout(): void {
    localStorage.clear();
  }

  public setBrandsKey(brandIDArray: string[]): void {
    localStorage.setItem(this.brandsKey, brandIDArray.toString());
  }

  public hasRole(roleName: RoleName): boolean {
    const personLogin = this.getCachedLogin();
    if (!personLogin || !personLogin.roles || personLogin.roles.length === 0) {
      return false;
    } else {
      const role = personLogin.roles.filter(personRole => RoleName[personRole.name as keyof typeof RoleName] === roleName);
      return (role && role.length > 0);
    }
  }

  public isInternalOrRsm(): boolean {
    const personLogin = this.getCachedLogin();
    return !!(personLogin && (this.isInternalUser(personLogin.roles) || this.isRsmUser(personLogin.groups)));
  }

  isOutsideEngineer(): boolean {
    const personLogin = this.getCachedLogin();
    return !!(personLogin && personLogin.person && 'OUTENG' === personLogin.person.typeCode);
  }

  public isInternal(): boolean {
    const personLogin = this.getCachedLogin();
    return this.isInternalUser(personLogin!.roles);
  }

  private isInternalUser(roles: Role[]): boolean {
    return roles.length > 0;
  }

  public isRsm(): boolean {
    const personLogin = this.getCachedLogin();
    return this.isRsmUser(personLogin!.groups);
  }

  private isRsmUser(groups: Group[]): boolean {
    const rsmSuffixes: string[] = ['REP', 'OEM'];
    const rsmPrefixes: string[] = ['RST'];
    return groups.filter(group => this.hasPrefix(group.name, rsmPrefixes) || this.hasSuffix(group.name, rsmSuffixes)).length > 0;
  }

  private hasPrefix(groupName: string, prefixes: string[]): boolean {
    for (const prefix of prefixes) {
      if (groupName.lastIndexOf(prefix, 0) === 0) {
        return true;
      }
    }
    return false;
  }

  private hasSuffix(groupName: string, suffixes: string[]): boolean {
    for (const suffix of suffixes) {
      if (groupName.indexOf(suffix, groupName.length - suffix.length) > -1) {
        return true;
      }
    }
    return false;
  }

  isLoginExpired(): boolean {
    const expiration = localStorage.getItem(this.expirationKey);
    if (!expiration || isNaN(+expiration)) {
      return undefined!;
    }
    return new Date().getTime() > +expiration;
  }

  setApplicationCredential(credential: string): void {
    this.applicationCredential = credential;
  }

  getApplicationCredential(): string | undefined {
    return this.applicationCredential;
  }

  isAdmin(): boolean {
    return this.hasRole(RoleName.IT);
  }

  isBetaTester(): boolean {
    const personLogin = this.getCachedLogin();
    if (!personLogin || !personLogin.groups) {
      return false;
    } else {
      return personLogin.groups.filter(group => 'BETA_TEST' === group.name).length === 1;
    }
  }

}
